@if (!onlyForm()) {
  <rt-dialog-header [heading]="headerText() || ('app.add_candidate_form.dialog_head' | translate)">
    @if (headerButton()) {
      <rt-button variant="primary" [loading]="loading" [disabled]="disabled" (click)="submit()">
        @if (buttonIcon) {
          <rt-icon [name]="buttonIcon" />
        }
        <span> {{ buttonText() || ('app.common.add' | translate) }}</span>
      </rt-button>
    }
  </rt-dialog-header>
}

<rt-dialog-body class="no-padding" [formGroup]="candidateForm">
  <div layout-horizontal margin-bottom space-lr>
    <div class="avatar-wrapper">
      <rt-avatar
        [size]="'large'"
        [shape]="'oval'"
        [imageSrc]="getControl('photoUrl').value || photoUrl || null"
        [initials]="avatarInitial()"
        [iconName]="'image'"
        [type]="resolveAvatarType()"
        customIconSize="small"
        class="avatar"
      />
      <rt-group>
        <rt-icon-button
          variant="tertiary"
          [disabled]="loading"
          [class.centered]="!(photoUrl && hasAvatarImage)"
          [rtTooltip]="'app.candidate.avatar.upload_tooltip' | translate"
          (click)="photoFileInput.click()"
          iconName="file-upload"
          shape="round"
          class="upload-button avatar-action-button"
        />
        @if (photoUrl && hasAvatarImage) {
          <rt-icon-button
            variant="tertiary"
            [disabled]="loading"
            [rtTooltip]="'app.candidate.avatar.delete_tooltip' | translate"
            (click)="photoChanged(null)"
            iconName="delete"
            shape="round"
            class="delete-button avatar-action-button danger-on-hover"
          />
        }

        <input
          #photoFileInput
          class="visually-hidden"
          type="file"
          accept="image/*"
          (change)="photoChanged(photoFileInput.files)"
        />
      </rt-group>
    </div>

    <rt-candidate-personal-details-form
      [candidate]="candidate()"
      [detailsForm]="detailsForm"
      layout-full-width
    />
  </div>

  <rt-heading variant="subheading uppercased" space-lr>
    {{ 'app.add_candidate_form.jobs_or_talent_pools' | translate }}
  </rt-heading>

  @for (job of candidateJobs; track trackByOffer($index, job)) {
    <div>
      <div layout-horizontal layout-align-items-center padding-tb-small-xx space-lr class="offer">
        <div layout>
          <rt-offer-status-indicator
            margin-right-small-x
            [offerStatus]="job"
            class="job-status-indicator"
          />
          <div layout-vertical>
            <rt-heading>
              {{ job.title }}
            </rt-heading>
            @if (job.kind === 'job') {
              <rt-candidate-work-locations-button
                [locationIds]="getControl('jobs').value[$index].locationIds"
                [canEdit]="job.locationsEditable"
                (edit)="editLocations(job)"
              />
            }
          </div>
        </div>

        <rt-icon-button
          variant="plain"
          (click)="removeOffer(job.id)"
          iconName="close"
          shape="square"
        />
      </div>
      <rt-divider />
    </div>
  }
  @for (talentPool of candidateTalentPools; track trackByOffer($index, talentPool)) {
    <div>
      <div layout-horizontal layout-align-items-center padding-tb-small-xx space-lr class="offer">
        <div layout-horizontal>
          <rt-offer-status-indicator margin-right-small-x [offerStatus]="talentPool" />
          <rt-heading>
            {{ talentPool.title }}
          </rt-heading>
        </div>

        <rt-icon-button
          variant="plain"
          (click)="removeOffer(talentPool.id)"
          iconName="close"
          shape="square"
        />
      </div>
      <rt-divider />
    </div>
  }

  <rt-group margin-top-small margin-bottom space-lr>
    <rt-button (click)="openOffersAssignments()" variant="tertiary">
      <rt-icon name="add" />
      <span>{{ 'app.add_candidate_form.jobs_or_talent_pools_assign' | translate }}</span>
    </rt-button>
  </rt-group>

  <rt-divider variant="line" />

  @if (warningMessage) {
    <rt-message variant="context-warning small" margin>
      {{ warningMessage }}
    </rt-message>
  }

  <rt-candidate-tags-typeahead-wrapper
    [tags]="{
      value: candidateForm.get(['basic', 'tags'])?.value || [],
      editable: true,
      writable: abilities.manageAttributes,
    }"
    (save)="addTag($event)"
    (delete)="deleteTag($event)"
    margin-tb-small
    space-lr
  >
    <rt-heading heading variant="subheading" padding-right-medium layout-flex-1-0>
      {{ 'app.common.tags' | translate }}
    </rt-heading>
  </rt-candidate-tags-typeahead-wrapper>

  <rt-divider />

  <div class="wrapper-candidate-profile-information">
    <rt-add-candidate-profile-information
      #candidateInfo
      [validationErrors]="validationServerErrors"
      [boxStyle]="'transparent'"
      [visibleFields]="visibleFields"
      [actionsVisible]="false"
      [showHeading]="false"
      [editable]="true"
      [editing]="true"
      [permanentlyEditable]="true"
      [information]="candidateInformation"
      (valueChanges)="candidateProfileInfoValueChanges()"
    />

    <rt-group margin-top-small margin-bottom-small space-lr>
      <rt-button variant="plain" (click)="toggleVisibleFields()">
        <rt-icon [name]="!showAllFields ? 'keyboard-arrow-down' : 'keyboard-arrow-up'" />
        <span>{{
          (!showAllFields
            ? 'app.add_candidate_form.show_more_fields'
            : 'app.add_candidate_form.show_less_fields'
          ) | translate
        }}</span>
      </rt-button>
    </rt-group>
  </div>

  <rt-divider variant="line" />

  <rt-candidate-sources-typeahead-wrapper
    [sources]="{
      value: candidateForm.get(['basic', 'sources'])!.value,
      editable: true,
      writable: abilities.manageAttributes,
    }"
    (saveSource)="addSource($event)"
    (deleteSource)="deleteSource($event)"
    margin-left
  />

  <rt-divider variant="line" margin-bottom />

  @if (!disallowCvUpdate()) {
    <rt-heading variant="subheading uppercased" margin-bottom-small space-lr>
      {{ 'app.candidate.cv' | translate }}
    </rt-heading>
  }
  @if (!disallowCvUpdate()) {
    <rt-group margin-bottom-small space-lr>
      @if (getControl('attachment').value) {
        <rt-button variant="tertiary" (click)="cvFileHandler.instance.readFileFromDisk()">
          <span>{{ getControl('attachment').value.filename }}</span>
        </rt-button>
      }
      @if (!getControl('attachment').value) {
        <rt-button
          variant="tertiary"
          (click)="cvFileHandler.instance.readFileFromDisk()"
          class="cv-upload-btn"
        >
          @if (!getControl('cv').value) {
            <rt-icon name="file-upload" />
          }
          @if (!getControl('cv').value) {
            <rt-truncate>{{ 'app.candidate.upload_file' | translate }}</rt-truncate>
          }
          @if (getControl('cv').value) {
            <rt-truncate>{{ cvFileName }} </rt-truncate>
          }
        </rt-button>
      }
      @if (getControl('cv').value || getControl('attachment').value) {
        <rt-icon-button
          variant="plain"
          iconName="close"
          shape="square"
          (click)="cvChanged({ files: null, status: ReadStatus.OK })"
        >
          <rt-icon name="close" />
        </rt-icon-button>
      }
      <rt-file-handler #cvFileHandler accept="cv" (nextFile)="cvChanged($event)" />
    </rt-group>
  }

  <rt-heading variant="subheading uppercased" margin-bottom-small space-lr>
    {{ 'app.candidate.cover_letter' | translate }}
  </rt-heading>
  <div layout-full-width space-lr>
    @if (!isCoverLetterSet) {
      <rt-cover-letter-type (selected)="onCoverLetterTypeSelected($event)" />
    }
    <rt-file-handler
      #coverLetterFileHandler
      accept="cl"
      (nextFile)="onNextCoverLetterFile($event)"
    />
    @if (coverLetterType === CoverLetterType.FILE && coverLetterFileName) {
      <rt-cover-letter-file
        [fileName]="coverLetterFileName"
        (fileChange)="onCoverLetterFileChange()"
        (closed)="onCoverLetterFileRemoved()"
      />
    }
    @if (coverLetterType === CoverLetterType.TEXT) {
      <div class="textarea-icon-in-top-right-container">
        <rt-textarea
          [formControl]="getControl('coverLetter')"
          [autoHeight]="true"
          [rtProtectedControl]="getControl('coverLetter')"
          [autoFocus]="focusOnCoverLetter()"
          [hasRightPadding]="true"
        />
        <rt-icon-button
          iconName="close"
          variant="plain"
          shape="square"
          size="small"
          (click)="onCoverLetterTextClose()"
        />
      </div>
    }
  </div>
</rt-dialog-body>

@if (!onlyForm()) {
  <rt-dialog-footer>
    <rt-button variant="plain" [disabled]="loading" (click)="cancelClick.emit()" margin-right-small>
      {{ 'app.common.cancel' | translate }}
    </rt-button>
    <rt-button variant="primary" [loading]="loading" [disabled]="disabled" (click)="submit()">
      @if (buttonIcon) {
        <rt-icon [name]="buttonIcon" />
      }
      <span>{{ buttonText() || ('app.common.add' | translate) }}</span>
    </rt-button>
  </rt-dialog-footer>
}
